export const namespaced = true;

/**
 * Estado de la apertura de la tripartita, tal como lo sirve
 * `RenderCoacheeAccelerators.tripartiteOpening.status`.
 *
 * Arranca en `null` a propósito: antes de que resuelva ninguna query no se
 * muestra nada. Ver `hasPendingOpening`.
 */
export const state = () => ({
  openingStatus: null,
  // Fin de la ventana de 21 días. Viaja como fecha y no como días restantes:
  // los días se cuentan en la zona del viajero y un número servido y cacheado
  // envejecería mal.
  openingWindowEndsAt: null,
});

// Único valor que significa "pendiente". No se declara una lista de valores
// "resueltos": esa lista siempre se queda corta el día que la API agrega un
// estado nuevo.
const OPENING_ACTIVE = "ACTIVE";

export const mutations = {
  SET_OPENING_STATUS(state, status) {
    state.openingStatus = status;
  },
  SET_OPENING_WINDOW_ENDS_AT(state, windowEndsAt) {
    state.openingWindowEndsAt = windowEndsAt || null;
  },
};

export const getters = {
  /**
   * ¿Hay una apertura de tripartita sin resolver? (US-2)
   *
   * Solo gobierna si se MUESTRA la card recordatoria. No bloquea nada: el
   * principio rector del PRD es que la tripartita es "recomendada pero nunca
   * bloqueante", y US-2 lo dice explícito — "el botón de sesión normal sigue
   * disponible en paralelo".
   *
   * Se llamaba `isSchedulingGated` cuando deshabilitaba el CTA de agendar. Ese
   * bloqueo se revirtió por decisión de producto, y el nombre viejo habría
   * quedado describiendo algo que el getter ya no hace.
   *
   * Una sola comparación positiva contra ACTIVE: todo lo demás —estado inicial,
   * `null`, query caída, un valor de enum que todavía no conocemos, o un
   * programa sin la feature— cae en la misma rama y no muestra la card.
   */
  hasPendingOpening: (state) => state.openingStatus === OPENING_ACTIVE,
  openingWindowEndsAt: (state) => state.openingWindowEndsAt,
};

// No hay getter para "ventana vencida": el único consumidor del mensaje
// post-ventana es `Accelerators-v1.vue`, que ya tiene el `status` en su propio
// computed desde la query de accelerators. Un getter aquí sería una segunda
// fuente del mismo dato, sin nadie que la lea.

export const actions = {
  /**
   * Carga el estado de la apertura para el gate de agendamiento (US-11).
   *
   * Reutiliza la query de accelerators en vez de crear una propia: el estado ya
   * viaja ahí y dos queries al mismo dato es como card y gate empiezan a
   * discrepar.
   *
   * FAIL-OPEN: cualquier problema —la query falla, la respuesta no trae
   * `tripartiteOpening`, o resuelve vacío— deja el status en `null`, que el
   * getter lee como "no bloquear". El error NO se propaga: un fallo de red al
   * cargar un dato de una feature opcional no puede reventar la página de
   * sesiones de un cliente que ni siquiera la tiene activa.
   *
   * @param {Object} ctx
   * @param {Object} payload
   * @param {Object} payload.apolloClient
   * @param {string} payload.userId
   * @param {string} payload.programId
   */
  async fetchOpeningStatus(
    { commit, dispatch },
    { apolloClient, userId, programId }
  ) {
    try {
      const accelerators = await dispatch(
        "accelerators/fetchRenderCoacheeAccelerators",
        { apolloClient, userId, programId },
        { root: true }
      );
      commit(
        "SET_OPENING_STATUS",
        accelerators?.tripartiteOpening?.status ?? null
      );
      commit(
        "SET_OPENING_WINDOW_ENDS_AT",
        accelerators?.tripartiteOpening?.windowEndsAt ?? null
      );
      return true;
    } catch (error) {
      // Se traga a propósito: ver FAIL-OPEN arriba. El error NO se propaga —
      // un fallo de red no puede romper la carga de la página ni bloquear el
      // agendamiento.
      //
      // Pero devuelve `false` para que quien necesite distinguir "refresqué" de
      // "no pude" lo sepa: tras omitir, el llamador avisa que recargue. Sin
      // esto su `catch` nunca se ejecutaba y el aviso era código muerto.
      commit("SET_OPENING_STATUS", null);
      commit("SET_OPENING_WINDOW_ENDS_AT", null);
      return false;
    }
  },

  /**
   * Omite la tripartita de una fase con un motivo (US-5). No bloquea el proceso.
   * @param {Object} _ctx
   * @param {Object} payload
   * @param {string} payload.programId
   * @param {string} [payload.kind="opening"]
   * @param {string} payload.category
   * @param {string} [payload.comment]
   * @param {Object} payload.restClient - instancia REST inyectada
   */
  async omitTripartite(
    _ctx,
    { programId, kind = "opening", category, comment = "", restClient }
  ) {
    const { data } = await restClient.post("/api/v1/tripartite/omit", {
      programId,
      kind,
      category,
      comment,
    });
    return data;
  },
};
